import React, { memo, useState, useMemo } from 'react';
import { NodeProps, useEdges, Position } from 'reactflow';
import { Box, Typography, Paper, Chip, IconButton, TextField, Tooltip } from '@mui/material';
import { Timeline as CaboIcon, Delete as DeleteIcon, Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
// Validação centralizada no React Flow

import CaboFiberGroups from './CaboFiberGroups';
import type { CaboTipo, PadraoTuboLoose } from '../../../services/caboService';
import { useDiagramInteractive } from '../../../utils/useDiagramInteractive';

interface CaboNodeData {
  id: number;
  cabo_id: number;
  titulo: string;
  qtde_fo: number;
  color: string;
  cabo_tipo?: CaboTipo | null;
  padrao_tubo_loose?: PadraoTuboLoose;
  qtde_tubo_loose?: number;
  observacao?: string;
  portas_tipo?: Record<string, 'in' | 'out'>; // '1' -> 'in' | 'out' por fibra (caboin default: out)
  onDelete?: (id: number) => void;
  onEditCabo?: (caboId: number) => void;
  onUpdateObservacao?: (id: number, observacao: string) => void;
  onUpdatePortaTipo?: (splitterId: number, numeroFibra: number, tipo: 'in' | 'out') => void;
  opticalSignal?: {
    inputSignal: number | null;
    outputSignals: Record<string, number>;
  };
}

const CaboNode: React.FC<NodeProps<CaboNodeData>> = ({ data, selected, id }) => {
  const handleColor = data.color || '#424242';
  const edges = useEdges();
  const isDiagramInteractive = useDiagramInteractive();

  // Portas que têm alguma conexão (fusão), neste nó ou em outro: ocultar botão alterar
  const portasComFusao = useMemo(() => {
    const set = new Set<number>();
    edges.forEach(edge => {
      if (edge.source === id) {
        const m = edge.sourceHandle?.match(/(?:output|input)-fo(\d+)/i);
        if (m) set.add(parseInt(m[1], 10));
      }
      if (edge.target === id) {
        const m = edge.targetHandle?.match(/(?:output|input)-fo(\d+)/i);
        if (m) set.add(parseInt(m[1], 10));
      }
    });
    return set;
  }, [edges, id]);

  // Estado para edição de observação
  const [isEditingObservacao, setIsEditingObservacao] = useState(false);
  const [observacaoTemp, setObservacaoTemp] = useState(data.observacao || '');

  // Handler para deletar o cabo
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation(); // Evitar que o clique propague para o nó
    if (data.onDelete) {
      data.onDelete(data.id);
    }
  };

  const handleEditCabo = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data.onEditCabo && data.cabo_id) {
      data.onEditCabo(data.cabo_id);
    }
  };

  // Handler para iniciar edição de observação
  const handleEditObservacao = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingObservacao(true);
    setObservacaoTemp(data.observacao || '');
  };

  // Handler para salvar observação
  const handleSaveObservacao = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data.onUpdateObservacao) {
      data.onUpdateObservacao(data.id, observacaoTemp);
    }
    setIsEditingObservacao(false);
  };

  // Handler para cancelar edição
  const handleCancelObservacao = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditingObservacao(false);
    setObservacaoTemp(data.observacao || '');
  };

  // Função para verificar se um handle tem conexão
  const isHandleConnected = (handleId: string, handleType: 'source' | 'target'): boolean => {
    const connectionCount = edges.filter(edge => {
      if (handleType === 'source') {
        return edge.source === id && edge.sourceHandle === handleId;
      } else {
        return edge.target === id && edge.targetHandle === handleId;
      }
    }).length;

    // Permitir múltiplas conexões (limite alto para flexibilidade solicitada pelo usuário)
    const maxConnections = 5;
    return connectionCount >= maxConnections;
  };

  // portas_tipo: caboin default todas 'out', só exibir o handle correspondente
  const portasTipo = data.portas_tipo ?? Object.fromEntries(Array.from({ length: data.qtde_fo }, (_, i) => [String(i + 1), 'out' as const]));

  return (
    <Paper
      elevation={selected ? 8 : 3}
      sx={{
        minWidth: 220,
        width: 'max-content',
        maxWidth: 280,
        border: selected ? `2px solid ${handleColor}` : `1px solid ${handleColor}`,
        borderRadius: 2,

        backgroundColor: `${handleColor}15`,
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'visible',
        boxSizing: 'border-box',
        '&:hover': isDiagramInteractive ? {
          '& .delete-button': {
            opacity: 1
          },
          '& .edit-cabo-button': {
            opacity: 1
          }
        } : {},
      }}
    >
      {/* Barra superior com cor do cabo */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: handleColor
        }}
      />

      {isDiagramInteractive && (
      <>
      <Tooltip title="Editar cabo" arrow>
        <IconButton
          className="edit-cabo-button"
          onClick={handleEditCabo}
          size="small"
          sx={{
            position: 'absolute',
            top: -8,
            left: -8,
            backgroundColor: '#1976d2',
            color: 'white',
            width: 20,
            height: 20,
            opacity: 0,
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: '#1565c0',
              transform: 'scale(1.1)'
            },
            zIndex: 10
          }}
        >
          <EditIcon sx={{ fontSize: 12 }} />
        </IconButton>
      </Tooltip>

      <IconButton
        className="delete-button"
        onClick={handleDelete}
        size="small"
        sx={{
          position: 'absolute',
          top: -8,
          right: -8,
          backgroundColor: '#f44336',
          color: 'white',
          width: 20,
          height: 20,
          opacity: 0,
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: '#d32f2f',
            transform: 'scale(1.1)'
          },
          zIndex: 10
        }}
      >
        <DeleteIcon sx={{ fontSize: 12 }} />
      </IconButton>
      </>
      )}

      <Box
        sx={{
          p: 1.5,
          pr: 0.75,
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 0.5,
          overflow: 'visible',
        }}
      >
        {/* Ícone e tipo - usa cor do cabo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            width: '100%',
            px: 1,
            py: 0.5,
            borderRadius: 1,
            backgroundColor: `${handleColor}15`,
            borderLeft: `3px solid ${handleColor}`
          }}
        >
          <CaboIcon sx={{ fontSize: 18, color: handleColor }} />
          <Chip
            label="CABO"
            size="small"
            sx={{
              backgroundColor: handleColor,
              color: 'white',
              fontSize: '0.7rem',
              height: 20
            }}
          />
        </Box>

        {/* Título do cabo */}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 'bold',
            textAlign: 'center',
            fontSize: '0.75rem',
            color: '#333',
            lineHeight: 1.2,
            maxWidth: '140px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {data.titulo}
        </Typography>

        <CaboFiberGroups
          nodeId={id}
          splitterId={data.id}
          qtde_fo={data.qtde_fo}
          padrao_tubo_loose={data.padrao_tubo_loose ?? 'abnt'}
          qtde_tubo_loose={data.qtde_tubo_loose ?? 1}
          cabo_tipo={data.cabo_tipo}
          portas_tipo={portasTipo}
          defaultPortaTipo="out"
          handleColor={handleColor}
          portasComFusao={portasComFusao}
          isHandleConnected={isHandleConnected}
          onUpdatePortaTipo={data.onUpdatePortaTipo}
          handlePosition={Position.Right}
          opticalSignal={data.opticalSignal}
        />

        {/* Seção de observação */}
        <Box sx={{
          width: '100%',
          mt: 1,
          borderTop: '1px solid #e9ecef',
          pt: 1
        }}>
          {!isEditingObservacao ? (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              minHeight: 24
            }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '0.6rem',
                  color: data.observacao ? '#495057' : '#adb5bd',
                  fontStyle: data.observacao ? 'normal' : 'italic',
                  flex: 1,
                  textAlign: 'left',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '100px'
                }}
              >
                {data.observacao || 'Sem observações'}
              </Typography>
              {isDiagramInteractive && (
              <IconButton
                onClick={handleEditObservacao}
                size="small"
                sx={{
                  width: 16,
                  height: 16,
                  ml: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.04)'
                  }
                }}
              >
                <EditIcon sx={{ fontSize: 10 }} />
              </IconButton>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <TextField
                value={observacaoTemp}
                onChange={(e) => setObservacaoTemp(e.target.value)}
                placeholder="Digite uma observação..."
                multiline
                rows={2}
                variant="outlined"
                size="small"
                autoFocus
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.65rem',
                    '& fieldset': {
                      borderColor: '#ced4da',
                    },
                    '&:hover fieldset': {
                      borderColor: '#495057',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: handleColor,
                    },
                  },
                  '& .MuiInputBase-input': {
                    padding: '4px 6px',
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                <IconButton
                  onClick={handleCancelObservacao}
                  size="small"
                  sx={{
                    width: 16,
                    height: 16,
                    color: '#6c757d',
                    '&:hover': {
                      backgroundColor: 'rgba(108, 117, 125, 0.1)',
                      color: '#495057'
                    }
                  }}
                >
                  <CancelIcon sx={{ fontSize: 10 }} />
                </IconButton>
                <IconButton
                  onClick={handleSaveObservacao}
                  size="small"
                  sx={{
                    width: 16,
                    height: 16,
                    color: handleColor,
                    '&:hover': {
                      backgroundColor: `${handleColor}15`,
                    }
                  }}
                >
                  <SaveIcon sx={{ fontSize: 10 }} />
                </IconButton>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default memo(CaboNode); 